import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './components/AdminLayout';
import { API_ENDPOINTS } from '../config/api';

const ServiceManagement = () => {
  const [services, setServices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    price: ''
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching services from:', API_ENDPOINTS.ADMIN.SERVICES);
      
      const response = await axios.get(API_ENDPOINTS.ADMIN.SERVICES, {
        timeout: 8000,
      });
      
      setServices(response.data);
      setSelectedServices([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Error fetching services:', error);
      handleApiError(error, 'fetch services');
    } finally {
      setLoading(false);
    }
  };

  const handleApiError = (error, operation) => {
    if (error.code === 'ECONNABORTED') {
      setError(`Request timeout while trying to ${operation}. Please check your connection.`);
    } else if (error.response) {
      setError(`Server error: ${error.response.status}. Failed to ${operation}.`);
    } else if (error.request) {
      setError(`Network error. Please check if the server is running.`);
    } else {
      setError(`Unable to ${operation}. Please try again later.`);
    }
  };

  const showSuccess = (text) => {
    setSuccess(text);
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);

      if (isEditing) {
        // Update existing service
        await axios.put(`${API_ENDPOINTS.ADMIN.SERVICES}/${formData.id}`, formData, {
          timeout: 8000,
        });
        showSuccess('Service updated successfully!');
      } else {
        // Create new service
        await axios.post(API_ENDPOINTS.ADMIN.SERVICES, formData, {
          timeout: 8000,
        });
        showSuccess('Service added successfully!');
      }
      
      await fetchServices();
      resetForm();
    } catch (error) {
      console.error('Error saving service:', error);
      if (error.response && error.response.data.error) {
        setError(error.response.data.error);
      } else {
        handleApiError(error, isEditing ? 'update service' : 'add service');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (service) => {
    setFormData({
      id: service.id,
      name: service.name,
      description: service.description || '',
      price: service.price
    });
    setIsEditing(true);
    setShowForm(true);
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
      price: ''
    });
    setIsEditing(false);
    setShowForm(false);
    setError('');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        setError('');
        setLoading(true);
        await axios.delete(`${API_ENDPOINTS.ADMIN.SERVICES}/${id}`, {
          timeout: 8000,
        });
        
        await fetchServices();
        showSuccess('Service deleted successfully!');
      } catch (error) {
        console.error('Error deleting service:', error);
        handleApiError(error, 'delete service');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedServices.length === 0) {
      setError('Please select at least one service to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedServices.length} service(s)?`)) {
      return;
    }

    try {
      setError('');
      setLoading(true);
      await axios.post(`${API_ENDPOINTS.ADMIN.SERVICES}/bulk_delete`, {
        service_ids: selectedServices
      }, {
        timeout: 8000,
      });
      
      await fetchServices();
      showSuccess(`Successfully deleted ${selectedServices.length} service(s)`);
    } catch (error) {
      console.error('Error bulk deleting services:', error);
      handleApiError(error, 'delete services');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedServices([]);
    } else {
      setSelectedServices(services.map(service => service.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const isSelected = (serviceId) => selectedServices.includes(serviceId);

  return (
    <AdminLayout>
      <div className="admin-content">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Service Management</h2>
          <button 
            className="btn btn-primary" 
            onClick={handleAddNew}
            disabled={loading}
          >
            <i className="fas fa-plus me-2"></i>Add Service
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            {success}
            <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        {/* Bulk Actions Toolbar */}
        {selectedServices.length > 0 && (
          <div className="bulk-actions-toolbar mb-3 p-3 bg-primary text-white rounded d-flex justify-content-between align-items-center">
            <span>
              <strong>{selectedServices.length}</strong> service(s) selected
            </span>
            <div>
              <button 
                className="btn btn-danger btn-sm me-2"
                onClick={handleBulkDelete}
                disabled={loading}
                title="Delete Selected"
              >
                {loading ? (
                  <i className="fas fa-spinner fa-spin me-1"></i>
                ) : (
                  <i className="fas fa-trash me-1"></i>
                )}
                Delete Selected
              </button>
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => setSelectedServices([])}
                disabled={loading}
                title="Clear Selection"
              >
                <i className="fas fa-times me-1"></i>Clear
              </button>
            </div>
          </div>
        )}

        {showForm && (
          <div className="card form-container mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="card-title mb-0">
                  {isEditing ? 'Edit Service' : 'Add New Service'}
                </h4>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={resetForm}
                  disabled={loading}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">Service Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    disabled={loading}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    disabled={loading}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label className="form-label">Price (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                    disabled={loading}
                  />
                </div>
                <div className="d-flex gap-2">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {isEditing ? 'Updating...' : 'Saving...'}
                      </>
                    ) : (
                      isEditing ? 'Update Service' : 'Save Service'
                    )}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={resetForm}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">Services List</h5>
          </div>
          <div className="card-body">
            {loading && services.length === 0 ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading services...</p>
              </div>
            ) : services.length === 0 ? (
              <div className="text-center py-4">
                <i className="fas fa-concierge-bell fa-3x text-muted mb-3"></i>
                <p className="text-muted">No services found.</p>
                <button 
                  className="btn btn-primary"
                  onClick={handleAddNew}
                >
                  Add First Service
                </button>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead className="table-dark">
                    <tr>
                      <th width="50">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            id="selectAll"
                            disabled={loading}
                          />
                          <label className="form-check-label" htmlFor="selectAll"></label>
                        </div>
                      </th>
                      <th>ID</th>
                      <th>Service Name</th>
                      <th>Description</th>
                      <th>Price (₹)</th>
                      <th width="120">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map(service => (
                      <tr key={service.id} className={isSelected(service.id) ? 'table-active' : ''}>
                        <td>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={isSelected(service.id)}
                              onChange={() => handleSelectService(service.id)}
                              id={`service-${service.id}`}
                              disabled={loading}
                            />
                            <label className="form-check-label" htmlFor={`service-${service.id}`}></label>
                          </div>
                        </td>
                        <td>{service.id}</td>
                        <td>
                          <strong>{service.name}</strong>
                        </td>
                        <td>{service.description || <span className="text-muted">No description</span>}</td>
                        <td>
                          <span className="badge bg-success fs-6">₹{parseFloat(service.price).toFixed(2)}</span>
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button 
                              className="btn btn-outline-primary"
                              onClick={() => handleEdit(service)}
                              disabled={loading}
                              title="Edit Service"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button 
                              className="btn btn-outline-danger"
                              onClick={() => handleDelete(service.id)}
                              disabled={loading}
                              title="Delete Service"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .bulk-actions-toolbar {
          background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
        }
        .table-hover tbody tr:hover {
          background-color: rgba(0, 123, 255, 0.075);
        }
        .form-container {
          border-left: 4px solid #007bff;
        }
        .btn-group-sm .btn {
          padding: 0.25rem 0.5rem;
        }
      `}</style>
    </AdminLayout>
  );
};

export default ServiceManagement;